/**
 * Postal Code Checker Component
 *
 * A reusable component for checking utility eligibility by postal code.
 * Submits to the app proxy endpoint and emits events for parent components.
 * Uses utility-partner-manager for enrichment (single source of truth for provider data).
 * Caches results in UtilityStorage for restore without network requests.
 *
 * Events emitted:
 * - MysaEvents.postalCodeCheckStarted: Check started (for parent loading states)
 * - MysaEvents.postalCodeChecked: Postal code checked, enriched matches returned
 *
 * @example
 * // Listening to the event
 * import { MysaEvents } from '@theme/mysa-events';
 * document.addEventListener(MysaEvents.postalCodeChecked, (e) => {
 *   // Matches include full provider data: name, code, logoSvg, etc.
 *   console.log(e.detail.matches);
 * });
 */

import {
  MysaEvents,
  PostalCodeCheckStartedEvent,
  PostalCodeCheckedEvent,
} from "@theme/mysa-events";
import { createReporter } from "@theme/logger";
import { UtilityStorage } from "@theme/utility-storage";

const reportError = createReporter("PostalCodeChecker");

/** @typedef {import('./utility-partner-manager').EnrichedMatch} EnrichedMatch */

class PostalCodeChecker extends HTMLElement {
  /** @type {AbortController | null} - For aborting fetch requests */
  fetchController = null;

  /** @type {AbortController | null} - For cleaning up event listeners */
  eventController = null;

  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  input = null;

  /** @type {HTMLButtonElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  error = null;

  /** @type {HTMLElement | null} */
  loading = null;

  /** @type {import('./utility-partner-manager').UtilityPartnerManager | null} */
  #manager = null;

  /**
   * Last Check result for the deferred-wizard upgrade race. Private — callers
   * must `takeLastCheckedDetail()` so a remorph reconnect cannot peek a
   * leftover and treat it as a new lookup.
   * @type {import('./mysa-events').PostalCodeCheckedDetail | null}
   */
  #lastCheckedDetail = null;

  async connectedCallback() {
    // Create event controller for listener cleanup
    this.eventController = new AbortController();
    const { signal } = this.eventController;

    this.form = this.querySelector("form");
    this.input = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="postalInput"]')
    );
    this.button = this.querySelector('[ref="checkButton"]');
    this.error = this.querySelector('[ref="postalError"]');
    this.loading = this.querySelector('[ref="loading"]');

    // Do NOT touch the manager here: this element sits in the header on every
    // page, so importing/instantiating the manager on connect would pull its
    // ~20 KB gzip module onto every page. The manager is dynamically imported
    // and initialized in handleSubmit(), the only place that needs provider
    // data (enrichMatches).

    // Pre-populate input from saved postal code
    const savedData = UtilityStorage.get();
    if (this.input && savedData?.postalCode?.value) {
      this.input.value = savedData.postalCode.value;
    }

    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit.bind(this), {
        signal,
      });
    }

    this.querySelector('[ref="dismissBtn"]')?.addEventListener(
      "click",
      () => {
        this.dispatchEvent(
          new CustomEvent("utility-wizard:dismiss-requested", {
            bubbles: true,
          }),
        );
      },
      { signal },
    );
  }

  disconnectedCallback() {
    // Abort pending fetch requests
    this.fetchController?.abort();

    // Remove event listeners
    this.eventController?.abort();
  }

  /**
   * Dynamically import + initialize the utility partner manager. Called at
   * submit (not connect) so the manager only downloads when a lookup actually
   * happens, and its provider parse can overlap the postal fetch. Idempotent.
   * @returns {Promise<void>}
   */
  async #loadManager() {
    try {
      const { getUtilityPartnerManager } =
        await import("@theme/utility-partner-manager");
      const manager = getUtilityPartnerManager();
      await manager.initialize();
      this.#manager = manager;
    } catch (error) {
      // Chunk/network/init failure — keep #manager null so a successful
      // postal lookup still returns raw matches (enrichMatches degrades
      // when #manager is null). Assign only after initialize() resolves
      // so a rejected init cannot leave an uninitialized singleton set.
      this.#manager = null;
      console.error("[postal-code-checker] manager load failed", error);
    }
  }

  /**
   * Handle form submission
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (!this.input || !this.form) return;

    const postalCode = this.input.value?.trim();
    if (!postalCode) return;

    // Validate pattern
    const pattern = this.input.getAttribute("pattern");
    if (pattern) {
      const regex = new RegExp(pattern, "i");
      if (!regex.test(postalCode)) {
        this.showError("Please enter a valid postal code");
        return;
      }
    }

    this.hideError();
    this.setLoading(true);
    // Drop any unread stash so a remorph during this fetch cannot replay
    // the last completed Check as if it were new.
    this.#lastCheckedDetail = null;

    // Kick off the manager download + provider parse now (unawaited) so it
    // overlaps the postal lookup fetch below; awaited before enrichMatches.
    const managerReady = this.#loadManager();

    // Emit started event for parent components to show loading
    this.dispatchEvent(new PostalCodeCheckStartedEvent());

    // Abort any pending request
    if (this.fetchController) {
      this.fetchController.abort();
    }
    const controller = new AbortController();
    this.fetchController = controller;

    try {
      // Build URL from form action and FormData
      const formData = new FormData(this.form);
      const url = new URL(this.form.action, window.location.origin);
      for (const [key, value] of formData.entries()) {
        if (typeof value === "string") {
          url.searchParams.set(key, value);
        }
      }

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Enrich matches using utility-partner-manager (single source of truth).
      // #loadManager never rejects (it swallows load failures), so a manager
      // chunk/network failure can't fail an otherwise successful lookup — we
      // fall back to raw matches (enrichMatches degrades when #manager is null).
      await managerReady;
      const enrichedMatches = this.#manager
        ? this.#manager.enrichMatches(data.matches || [])
        : data.matches || [];

      // Cache enriched matches for restore without network request.
      // Cache empty arrays too so "no matches" persists across refresh.
      if (data.success) {
        UtilityStorage.setLastMatches(enrichedMatches, postalCode);
      }

      // Emit centralized event with enriched data. Stash on the element so
      // a wizard that upgrades after this dispatch can still render results.
      this.#emitChecked({
        postalCode,
        success: data.success,
        matches: enrichedMatches,
        message: data.message,
      });
    } catch (/** @type {unknown} */ error) {
      if (error instanceof Error && error.name === "AbortError") return;
      console.error("[PostalCodeChecker] Error:", error);
      reportError(error);
      this.showError("Unable to check postal code. Please try again.");

      // Dispatch error event so parent components can restore UI state
      this.#emitChecked({
        postalCode,
        success: false,
        matches: [],
        message: "Unable to check postal code. Please try again.",
      });
    } finally {
      // Only clear loading if this request is still the active one
      // Prevents race condition when rapid submissions abort previous requests
      if (this.fetchController === controller) {
        this.setLoading(false);
      }
    }
  }

  /**
   * Take (and clear) the last Check result. One-shot for the wizard upgrade
   * race — a later connectedCallback must not see this as a fresh lookup.
   * @returns {import('./mysa-events').PostalCodeCheckedDetail | null}
   */
  takeLastCheckedDetail() {
    const detail = this.#lastCheckedDetail;
    this.#lastCheckedDetail = null;
    return detail;
  }

  /**
   * Stash + dispatch a Check result so the wizard can render even if it
   * upgrades after this event has already fired.
   * @param {import('./mysa-events').PostalCodeCheckedDetail} detail
   */
  #emitChecked(detail) {
    this.#lastCheckedDetail = detail;
    this.dispatchEvent(new PostalCodeCheckedEvent(detail));
  }

  /**
   * Set loading state
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (this.loading) {
      this.loading.hidden = !isLoading;
    }
    if (this.button) {
      this.button.disabled = isLoading;
    }
  }

  /**
   * Show error message
   * @param {string} message
   */
  showError(message) {
    if (this.error) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    if (this.input) {
      this.input.classList.add("has-error");
    }
  }

  /** Hide error message */
  hideError() {
    if (this.error) {
      this.error.hidden = true;
    }
    if (this.input) {
      this.input.classList.remove("has-error");
    }
  }
}

if (!customElements.get("postal-code-checker")) {
  customElements.define("postal-code-checker", PostalCodeChecker);
}

export { PostalCodeChecker };
